export type Interpolation = 'Linear' | 'Step' | 'Bezier'

export interface BezierCurve {
  x1: number; y1: number
  x2: number; y2: number
}

export type EasingPreset = 'Linear' | 'EaseIn' | 'EaseOut' | 'EaseInOut' | 'Custom'

export interface Easing {
  preset: EasingPreset
  custom_bezier?: BezierCurve
}

export interface Keyframe {
  time: number
  value: number
  interpolation: Interpolation
  easing: Easing
}

export interface KeyframeTrack {
  property: string
  keyframes: Keyframe[]
}

export interface KeyframeTracks {
  tracks: KeyframeTrack[]
}

export const EASING_PRESETS: Record<EasingPreset, BezierCurve> = {
  Linear: { x1: 0, y1: 0, x2: 1, y2: 1 },
  EaseIn: { x1: 0.42, y1: 0, x2: 1, y2: 1 },
  EaseOut: { x1: 0, y1: 0, x2: 0.58, y2: 1 },
  EaseInOut: { x1: 0.42, y1: 0, x2: 0.58, y2: 1 },
  Custom: { x1: 0, y1: 0, x2: 1, y2: 1 },
}

function sampleBezierX(x1: number, x2: number, t: number): number {
  return 3 * (1 - t) * (1 - t) * t * x1 + 3 * (1 - t) * t * t * x2 + t * t * t
}

function sampleBezierY(y1: number, y2: number, t: number): number {
  return 3 * (1 - t) * (1 - t) * t * y1 + 3 * (1 - t) * t * t * y2 + t * t * t
}

function sampleBezierDX(x1: number, x2: number, t: number): number {
  return 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2)
}

export function evaluateBezierEasing(easing: Easing, t: number): number {
  const curve = easing.preset === 'Custom' && easing.custom_bezier
    ? easing.custom_bezier
    : EASING_PRESETS[easing.preset]

  let u = t
  for (let i = 0; i < 8; i++) {
    const x = sampleBezierX(curve.x1, curve.x2, u) - t
    const dx = sampleBezierDX(curve.x1, curve.x2, u)
    if (Math.abs(dx) < 1e-6) break
    u -= x / dx
  }
  u = Math.max(0, Math.min(1, u))
  return sampleBezierY(curve.y1, curve.y2, u)
}

export class KeyframeEvaluator {
  private cache: Map<string, number> = new Map()

  constructor(private tracks: KeyframeTracks) {}

  evaluate(property: string, time: number): number | undefined {
    const track = this.tracks.tracks.find(t => t.property === property)
    if (!track || track.keyframes.length === 0) return undefined

    const kfs = track.keyframes
    if (kfs.length === 1) return kfs[0].value
    if (time <= kfs[0].time) return kfs[0].value
    if (time >= kfs[kfs.length - 1].time) return kfs[kfs.length - 1].value

    let idx = this.cache.get(property) ?? 0

    if (idx < kfs.length - 1 && kfs[idx].time <= time && time < kfs[idx + 1].time) {
      // cache hit
    } else {
      let found = false
      for (let i = idx; i < Math.min(idx + 4, kfs.length - 1); i++) {
        if (kfs[i].time <= time && time < kfs[i + 1].time) {
          idx = i
          found = true
          break
        }
      }
      if (!found) {
        let lo = 0, hi = kfs.length - 2
        while (lo <= hi) {
          const mid = (lo + hi) >> 1
          if (kfs[mid].time <= time && time < kfs[mid + 1].time) {
            idx = mid
            break
          } else if (time < kfs[mid].time) {
            hi = mid - 1
          } else {
            lo = mid + 1
          }
        }
      }
    }

    this.cache.set(property, idx)

    const k1 = kfs[idx]
    const k2 = kfs[idx + 1]
    const dt = k2.time - k1.time
    if (dt <= 0) return k1.value
    const t = (time - k1.time) / dt

    switch (k2.interpolation) {
      case 'Step': return k1.value
      case 'Linear': return k1.value + (k2.value - k1.value) * t
      case 'Bezier': {
        const eased = evaluateBezierEasing(k2.easing, t)
        return k1.value + (k2.value - k1.value) * eased
      }
    }
  }

  clearCache() { this.cache.clear() }
}

export const ANIMATABLE_PROPERTIES: Record<string, {
  default: number; min: number; max: number; label: string
  step?: number; unit?: string; displayMultiplier?: number
}> = {
  brightness: { default: 1, min: 0.2, max: 3, label: 'Brightness', step: 0.01, unit: 'x', displayMultiplier: 1 },
  contrast: { default: 1, min: 0.2, max: 3, label: 'Contrast', step: 0.01, unit: 'x', displayMultiplier: 1 },
  saturation: { default: 1, min: 0, max: 3, label: 'Saturation', step: 0.01, unit: 'x', displayMultiplier: 1 },
  opacity: { default: 1, min: 0, max: 1, label: 'Opacity', step: 0.01, unit: '%', displayMultiplier: 100 },
  blur: { default: 0, min: 0, max: 20, label: 'Blur', step: 0.5, unit: 'px', displayMultiplier: 1 },
  vignette: { default: 0, min: 0, max: 1, label: 'Vignette', step: 0.01, unit: '%', displayMultiplier: 100 },
  scale: { default: 1, min: 0.1, max: 5, label: 'Scale', step: 0.01, unit: 'x', displayMultiplier: 1 },
  x: { default: 0, min: -100, max: 100, label: 'X Position', step: 1, unit: '%', displayMultiplier: 1 },
  y: { default: 0, min: -100, max: 100, label: 'Y Position', step: 1, unit: '%', displayMultiplier: 1 },
  rotation: { default: 0, min: -360, max: 360, label: 'Rotation', step: 1, unit: 'deg', displayMultiplier: 1 },
  bloom: { default: 0, min: 0, max: 1, label: 'Bloom', step: 0.01, unit: '%', displayMultiplier: 100 },
}

export const DEFAULT_EASING: Easing = { preset: 'Linear' }

export function addKeyframe(
  tracks: KeyframeTracks,
  property: string,
  time: number,
  value: number,
  interpolation: Interpolation = 'Linear',
  easing: Easing = DEFAULT_EASING,
): KeyframeTracks {
  const existing = tracks.tracks.find(t => t.property === property)
  const kf: Keyframe = { time, value, interpolation, easing }

  if (!existing) {
    return { tracks: [...tracks.tracks, { property, keyframes: [kf] }] }
  }

  const kfs = [...existing.keyframes]
  const existingIdx = kfs.findIndex(k => Math.abs(k.time - time) < 0.01)
  if (existingIdx >= 0) {
    kfs[existingIdx] = kf
  } else {
    kfs.push(kf)
    kfs.sort((a, b) => a.time - b.time)
  }

  return {
    tracks: tracks.tracks.map(t => t.property === property ? { ...t, keyframes: kfs } : t),
  }
}

export function removeKeyframe(tracks: KeyframeTracks, property: string, time: number): KeyframeTracks {
  const existing = tracks.tracks.find(t => t.property === property)
  if (!existing) return tracks

  const kfs = existing.keyframes.filter(k => Math.abs(k.time - time) >= 0.01)

  if (kfs.length === 0) {
    return { tracks: tracks.tracks.filter(t => t.property !== property) }
  }

  return {
    tracks: tracks.tracks.map(t => t.property === property ? { ...t, keyframes: kfs } : t),
  }
}

export function updateKeyframe(
  tracks: KeyframeTracks,
  property: string,
  index: number,
  updates: Partial<Keyframe>,
): KeyframeTracks {
  const existing = tracks.tracks.find(t => t.property === property)
  if (!existing || index < 0 || index >= existing.keyframes.length) return tracks

  const kfs = [...existing.keyframes]
  kfs[index] = { ...kfs[index], ...updates }
  kfs.sort((a, b) => a.time - b.time)

  return {
    tracks: tracks.tracks.map(t => t.property === property ? { ...t, keyframes: kfs } : t),
  }
}

export function getKeyframeAtTime(tracks: KeyframeTracks, property: string, time: number): Keyframe | undefined {
  const track = tracks.tracks.find(t => t.property === property)
  if (!track) return undefined
  return track.keyframes.find(k => Math.abs(k.time - time) < 0.01)
}

export function hasKeyframesForProperty(tracks: KeyframeTracks, property: string): boolean {
  const track = tracks.tracks.find(t => t.property === property)
  return !!track && track.keyframes.length > 0
}
